import React from 'react';
import CountUp from '../ui/CountUp';
import { getLiveStats } from '../../data/liveStats';
import { HOME_DESIGN_COUNT } from '../../data/siteStats';

/**
 * Trust strip - sits between the hero (dark video) and the live data strip.
 * Slim photo+logo-led trust signal. We render every logo at a consistent
 * height (h-9 sm:h-10) and let widths flow naturally so the strip feels
 * like a magazine masthead, not a mismatched grid.
 *
 * Counters (2026-07-05 rebrand - no builder counts, no years of
 * experience, no city-specific labels):
 *   - Home designs: sourced from siteStats.HOME_DESIGN_COUNT (SSOT)
 *   - Community members: +5 per day (from liveStats.js)
 *   - Families helped: +3 per month (from liveStats.js)
 *   - Independent guidance: static 100 (rendered with a "%" suffix)
 * Precise (non-round) values + visible count-up animation read as a real
 * dashboard, not the AI-slop "1000+ / 25+" placeholder pattern.
 */
const buildCounters = () => {
  const s = getLiveStats();
  return [
    { value: HOME_DESIGN_COUNT,  suffix: '+', label: 'Home designs in one place' },
    { value: s.communityMembers, suffix: '',  label: 'Community members' },
    { value: s.familiesHelped,   suffix: '+', label: 'Families helped' },
    { value: 100,                suffix: '%', label: 'Independent guidance' },
  ];
};

// Per-logo size classes. Some logos are small SVG-style marks (Awards Australia,
// Homely, Elite Agent, UniSA) and need to render slightly larger to match the
// optical weight of the photographic-style logos (7NEWS, BNA, HIA).
const logoStrip = [
  { name: "7NEWS", src: "/images/awards/7news-logo.png", alt: "Featured on 7NEWS", to: "/awards", className: "h-12 sm:h-16" },
  { name: "BNA Finalist", src: "/images/awards/bna-logo.jpeg", alt: "Business News Awards finalist", to: "/awards", className: "h-9 sm:h-10" },
  { name: "Awards Australia", src: "/images/awards/awards-australia-logo.png", alt: "Awards Australia", to: "/awards", className: "h-16 sm:h-20" },
  { name: "HIA", src: "/images/awards/hia-logo.png", alt: "Housing Industry Association", to: "/awards", className: "h-12 sm:h-16" },
  { name: "CBS", src: "/images/awards/cbs-logo.png", alt: "Consumer & Business Services", to: "/awards", className: "h-12 sm:h-16" },
  { name: "UniSA", src: "/images/awards/unisa-logo.png", alt: "University of South Australia", to: "/awards", className: "h-16 sm:h-20" },
  { name: "Melbourne Build 2026", src: "/images/awards/melbourne-build-2026-logo.png", alt: "Melbourne Build 2026 Expo", to: "/awards", className: "h-16 sm:h-20" },
  { name: "Small Business Champion 2026 Finalist", src: "/images/awards/champion-awards-2026-finalist.webp", alt: "Australian Professionals Small Business Champion Awards 2026 Finalist", to: "/awards", className: "h-16 sm:h-20" },
  { name: "Houzz", src: "/images/media/houzz-logo.png", alt: "Featured on Houzz", to: "/media", className: "h-12 sm:h-16" },
  { name: "Domain", src: "/images/media/domain-logo.png", alt: "Featured on Domain", to: "/media", className: "h-9 sm:h-11" },
  { name: "Homely", src: "/images/media/homely-logo.svg", alt: "Featured on Homely", to: "/media", className: "h-12 sm:h-14" },
  { name: "Elite Agent", src: "/images/media/eliteagent-logo.png", alt: "Featured in Elite Agent magazine", to: "/media", className: "h-16 sm:h-20" },
  { name: "Home Building Hub", src: "/images/partners/home-building-hub-logo.png", alt: "Home Building Hub podcast partner", to: "/watch", className: "h-12 sm:h-16" },
  // Store badges are served locally (downloaded from Apple/Google) so they
  // load instantly with the rest of the strip - the external URLs were the
  // cause of the slow/glitchy marquee start. Padding ratio note still applies:
  // Google's PNG carries ~32.8% transparent padding, Apple's SVG has none, so
  // Apple renders at ~0.67x Google's height to match the visible black box.
  { name: "App Store", src: "/images/app/app-store-badge.svg", alt: "Download BuildPilot on the Apple App Store", to: "https://apps.apple.com/au/app/buildpilot/id6477372083", className: "h-11 sm:h-14" },
  { name: "Google Play", src: "/images/app/google-play-badge.png", alt: "Get BuildPilot on Google Play", to: "https://play.google.com/store/apps/details?id=com.buildpilot.android&hl=en_US", className: "h-16 sm:h-20", wrapperClassName: "-ml-12 sm:-ml-16" },
];

const TrustStrip = () => {
  // Compute counters once per mount. (They're stable across the day, but
  // recomputing on remount picks up a date rollover without a page reload.)
  const counters = React.useMemo(buildCounters, []);

  // 2026-02-20 - hold the marquee at frame-0 until every logo has decoded.
  // Without this, on a cold cache the strip can flash partial / empty slots
  // for ~200-600ms before each <img> pops in.
  const trackRef = React.useRef(null);
  const sectionRef = React.useRef(null);
  const [ready, setReady] = React.useState(false);

  // 2026-02 CWV fix - LCP was ~34s on mobile (PSI). Root cause: this
  // strip sits directly below the hero and eager-loads 30 logo <img>s
  // with fetchpriority="high", stealing bandwidth from the hero image
  // LCP paint. Solution: gate the logo <img>s behind an IntersectionObserver
  // so they only start downloading once the strip is within ~400px of
  // the viewport. Hero image gets first bite of the bandwidth pipe.
  // rootMargin generous so users scrolling fast still see logos before
  // the strip is on-screen.
  const [logosInView, setLogosInView] = React.useState(false);
  React.useEffect(() => {
    const el = sectionRef.current;
    if (!el) return undefined;
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      // No IO support (very old browsers / SSR prerender): load immediately.
      setLogosInView(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setLogosInView(true);
          io.disconnect();
        }
      },
      { rootMargin: '1600px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  React.useEffect(() => {
    if (!logosInView) return undefined;
    const root = trackRef.current;
    if (!root) return undefined;
    const imgs = Array.from(root.querySelectorAll('img'));
    if (imgs.length === 0) { setReady(true); return undefined; }
    let cancelled = false;
    const waitOne = (img) => (img.complete && img.naturalWidth > 0
      ? Promise.resolve()
      : new Promise((res) => {
          img.addEventListener('load', res, { once: true });
          img.addEventListener('error', res, { once: true });
        }));
    Promise.all(imgs.map(waitOne)).then(() => { if (!cancelled) setReady(true); });
    // Safety net: if something stalls > 1s, start the scroll anyway.
    const timer = window.setTimeout(() => { if (!cancelled) setReady(true); }, 1000);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [logosInView]);

  return (
    <section ref={sectionRef} className="bp-no-pad bg-white border-b border-gray-100" data-testid="trust-strip">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        {/* Counters */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 sm:gap-6 mb-5 sm:mb-10">
          {counters.map((c) => (
            <div key={c.label} className="text-center" data-testid={`trust-counter-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              <p className="text-[26px] sm:text-[36px] font-bold gradient-text-blue tracking-[-0.02em] leading-none mb-1">
                <CountUp value={c.value} suffix={c.suffix} />
              </p>
              <p className="text-[12px] sm:text-[12.5px] text-gray-500 leading-[1.4]">{c.label}</p>
            </div>
          ))}
        </div>

        {/* Logo lockup - scrolling marquee for visual continuity with builder logos. */}
        <div className="border-t border-gray-100 pt-7 sm:pt-8">
          <p className="text-[11px] font-semibold uppercase tracking-[0.07em] text-gray-400 text-center mb-5 sm:mb-6">
            As featured in & recognised by
          </p>
          <div
            className="trust-marquee relative overflow-hidden"
            data-testid="trust-marquee"
            style={{
              maskImage:
                'linear-gradient(to right, transparent 0, black 64px, black calc(100% - 64px), transparent 100%)',
              WebkitMaskImage:
                'linear-gradient(to right, transparent 0, black 64px, black calc(100% - 64px), transparent 100%)',
            }}
          >
            <div
              ref={trackRef}
              className={`trust-marquee__track flex items-center gap-x-12 sm:gap-x-16 py-2 ${ready ? 'is-ready' : ''}`}
            >
              {[...logoStrip, ...logoStrip].map((logo, idx) => {
                const key = `${logo.name}-${idx}`;
                return (
                  <div
                    key={key}
                    aria-label={logo.alt}
                    className={`shrink-0 flex items-center opacity-90${logo.wrapperClassName ? ` ${logo.wrapperClassName}` : ''}`}
                    data-testid={idx < logoStrip.length ? `trust-logo-${logo.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}` : undefined}
                  >
                    <img
                      src={logosInView ? logo.src : undefined}
                      alt={idx < logoStrip.length ? logo.alt : ''}
                      aria-hidden={idx >= logoStrip.length}
                      className={`${logo.className} w-auto object-contain`}
                      decoding="async"
                      fetchPriority="low"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes trust-marquee-scroll {
          0% { transform: translate3d(0, 0, 0); }
          100% { transform: translate3d(-50%, 0, 0); }
        }
        .trust-marquee__track {
          width: max-content;
          /* The track is visible immediately so logos fade in progressively
             as they decode (no all-or-nothing blank hold). Only the scroll
             animation waits for .is-ready, so it doesn't start half-empty. */
          animation: trust-marquee-scroll 38s linear infinite;
          animation-play-state: paused;
          will-change: transform;
        }
        .trust-marquee__track.is-ready {
          animation-play-state: running;
        }
        @media (prefers-reduced-motion: reduce) {
          .trust-marquee__track { animation: none; }
        }
      `}</style>
    </section>
  );
};

export default TrustStrip;
